import { RefObject } from "react"
import { Chat } from "@/reducers/chatsSlice"
import { Message } from "@/reducers/messagesSlice"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import { ChatInputTextAreaRef } from "@/components/ChatInput"
import WidgetTabs from "@/components/chat/WidgetTabs"
import ChatMap from "@/components/maps/ChatMap"
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable"
import { cn } from "@/lib/utils"

type Props = {
  chat: Chat
  routeJid: string | undefined
  containerRef: RefObject<HTMLDivElement>
  /** The workspace's tabbed panel: Chat with the driver, Automations, Map. */
  showWidgets: boolean
  /** Without the workspace, a driver room with a location keeps its map. */
  showMap: boolean
  onHideMap?: () => void
  driverVisibleMessages: Message[]
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  chatInputRef: RefObject<ChatInputTextAreaRef>
}

/**
 * Everything to the right of the conversation. The two layouts put different
 * things there — the widget panel or the tracking map — and the conversation
 * column itself is identical in both, so this is the only place that has to
 * know which layout is on screen.
 */
const ChatSidePanel = ({
  chat,
  routeJid,
  containerRef,
  showWidgets,
  showMap,
  onHideMap,
  driverVisibleMessages,
  onSend,
  chatInputRef,
}: Props) => {
  if (showWidgets) {
    return (
      <>
        <ResizableHandle
          className={cn(
            "bg-sidebar transition-colors hover:bg-border data-[resize-handle-state=drag]:bg-border data-[resize-handle-state=hover]:bg-border",
          )}
        />
        <ResizablePanel defaultSize={40} minSize={20}>
          <div className="h-full w-full bg-sidebar p-2 transition-colors">
            <WidgetTabs
              chat={chat}
              driverJid={routeJid || chat.jid}
              driverVisibleMessages={driverVisibleMessages}
              onSend={onSend}
              onAddAutomation={(prompt) => {
                chatInputRef.current?.setText(prompt)
              }}
            />
          </div>
        </ResizablePanel>
      </>
    )
  }

  if (routeJid && showMap) {
    return (
      <>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={40} minSize={20}>
          <ChatMap
            chat={chat}
            driverJid={routeJid}
            chatContainerRef={containerRef}
            onHide={onHideMap}
          />
        </ResizablePanel>
      </>
    )
  }

  return null
}

export default ChatSidePanel
